import Anthropic from '@anthropic-ai/sdk';
import type {
  Model,
  ModelMetadata,
  ModelProvider,
  ModelRequest,
  ModelSettingsReasoningEffort,
} from '../../model';
import {
  type AnthropicCompatibleClient,
  type AnthropicCompatibleFetch,
  AnthropicCompatibleMessagesModel,
} from '../anthropic-compatible/messages-model';
import {
  getDefaultMiniMaxBaseURL,
  getDefaultMiniMaxKey,
  getDefaultMiniMaxModel,
} from './defaults';
import { MINIMAX_ANTHROPIC_RAW_MODEL_EVENT_SOURCE } from './raw-model-events';

export type MiniMaxAnthropicFetch = AnthropicCompatibleFetch;
export type MiniMaxAnthropicClient = AnthropicCompatibleClient;

export type MiniMaxProviderOptions = {
  apiKey?: string;
  baseURL?: string;
  defaultModel?: string;
  fetch?: MiniMaxAnthropicFetch;
  anthropicClient?: MiniMaxAnthropicClient;
  modelMetadata?:
    | Record<string, ModelMetadata>
    | ((modelName: string) => ModelMetadata | undefined);
};

const MINIMAX_DEFAULT_M3_MAX_TOKENS = 131_072;
const MINIMAX_M3_MAX_OUTPUT_TOKENS = 524_288;
const MINIMAX_DEFAULT_M2_MAX_TOKENS = 65_536;
const MINIMAX_M2_MAX_OUTPUT_TOKENS = 204_800;
const MINIMAX_ANTHROPIC_TIMEOUT_MS = 60 * 60 * 1000;
const MINIMAX_M3_MULTIMODAL_METADATA = {
  imageInput: true,
  imageToolResult: 'native',
} as const satisfies NonNullable<ModelMetadata['multimodal']>;

export class MiniMaxProvider implements ModelProvider {
  #client?: MiniMaxAnthropicClient;
  #modelCache = new Map<string, Model>();
  #options: MiniMaxProviderOptions;

  constructor(options: MiniMaxProviderOptions = {}) {
    if (options.anthropicClient) {
      if (options.apiKey) {
        throw new Error('Cannot provide both apiKey and anthropicClient');
      }
      if (options.baseURL) {
        throw new Error('Cannot provide both baseURL and anthropicClient');
      }
      if (options.fetch) {
        throw new Error('Cannot provide both fetch and anthropicClient');
      }
      this.#client = options.anthropicClient;
    }
    this.#options = options;
  }

  getDefaultModelName(): string {
    return this.#options.defaultModel || getDefaultMiniMaxModel();
  }

  getModelMetadata(modelName?: string): ModelMetadata | undefined {
    const resolvedModelName = modelName || this.getDefaultModelName();
    return (
      resolveConfiguredModelMetadata(
        this.#options.modelMetadata,
        resolvedModelName,
      ) ?? getKnownMiniMaxModelMetadata(resolvedModelName)
    );
  }

  #getClient(): MiniMaxAnthropicClient {
    if (!this.#client) {
      this.#client = new Anthropic({
        apiKey: this.#options.apiKey ?? getDefaultMiniMaxKey() ?? null,
        authToken: null,
        baseURL: this.#options.baseURL ?? getDefaultMiniMaxBaseURL(),
        fetch: this.#options.fetch,
        timeout: MINIMAX_ANTHROPIC_TIMEOUT_MS,
      });
    }
    return this.#client;
  }

  async getModel(modelName?: string | undefined): Promise<Model> {
    const model = modelName || this.getDefaultModelName();
    const cacheKey = JSON.stringify([model, 'minimax_anthropic_messages']);

    const cachedModel = this.#modelCache.get(cacheKey);
    if (cachedModel) {
      return cachedModel;
    }

    const resolvedModel = new AnthropicCompatibleMessagesModel({
      baseURL: this.#options.baseURL ?? getDefaultMiniMaxBaseURL(),
      client: this.#getClient(),
      model,
      metadata: this.getModelMetadata(model),
      options: {
        providerName: 'MiniMax',
        rawModelEventSource: MINIMAX_ANTHROPIC_RAW_MODEL_EVENT_SOURCE,
        responseIdFallback: 'minimax-anthropic-response',
        defaultMaxTokens: getDefaultMiniMaxMaxTokens,
        maxOutputTokens: getMiniMaxMaxOutputTokens,
        getReasoningSettings: getMiniMaxReasoningSettings,
        supportsImageInput: isMiniMaxM3Model,
        imageUnsupportedMessage: (modelName) =>
          `MiniMax Anthropic Messages only supports image inputs with MiniMax-M3. Model ${modelName} does not support image inputs.`,
      },
    });
    this.#modelCache.set(cacheKey, resolvedModel);
    return resolvedModel;
  }

  /**
   * Closes cached model wrappers and clears cache.
   */
  async close(): Promise<void> {
    const cachedModels = Array.from(new Set(this.#modelCache.values()));
    this.#modelCache.clear();

    const closeErrors: unknown[] = [];
    await Promise.all(
      cachedModels.map(async (model) => {
        const maybeClose = (model as { close?: () => Promise<void> | void })
          .close;
        if (typeof maybeClose !== 'function') {
          return;
        }
        try {
          await maybeClose.call(model);
        } catch (error) {
          closeErrors.push(error);
        }
      }),
    );

    if (closeErrors.length === 1) {
      throw closeErrors[0];
    }
    if (closeErrors.length > 1) {
      const error = new Error('Failed to close MiniMaxProvider.');
      (error as Error & { causes?: unknown[] }).causes = closeErrors;
      throw error;
    }
  }
}

function resolveConfiguredModelMetadata(
  resolver: MiniMaxProviderOptions['modelMetadata'],
  modelName: string,
): ModelMetadata | undefined {
  if (!resolver) {
    return undefined;
  }
  if (typeof resolver === 'function') {
    return resolver(modelName);
  }
  return resolver[modelName] ?? resolver[modelName.toLowerCase()];
}

function getKnownMiniMaxModelMetadata(
  modelName: string,
): ModelMetadata | undefined {
  if (isMiniMaxM3Model(modelName)) {
    return {
      contextWindowTokens: 1_000_000,
      multimodal: MINIMAX_M3_MULTIMODAL_METADATA,
    };
  }
  if (isMiniMaxM2Model(modelName)) {
    return {
      contextWindowTokens: 204_800,
    };
  }
  return undefined;
}

function getDefaultMiniMaxMaxTokens(modelName: string): number {
  return isMiniMaxM3Model(modelName)
    ? MINIMAX_DEFAULT_M3_MAX_TOKENS
    : MINIMAX_DEFAULT_M2_MAX_TOKENS;
}

function getMiniMaxMaxOutputTokens(modelName: string): number {
  return isMiniMaxM3Model(modelName)
    ? MINIMAX_M3_MAX_OUTPUT_TOKENS
    : MINIMAX_M2_MAX_OUTPUT_TOKENS;
}

function getMiniMaxReasoningSettings(
  effort: ModelSettingsReasoningEffort | undefined,
  args: { model: string; request: ModelRequest },
): {
  thinking?: Record<string, unknown>;
} {
  if (effort === undefined || !isMiniMaxM3Model(args.model)) {
    return {};
  }
  if (effort === 'none' || effort === null) {
    return { thinking: { type: 'disabled' } };
  }
  return { thinking: { type: 'adaptive' } };
}

function isMiniMaxM3Model(modelName: string): boolean {
  return modelName.trim().toLowerCase() === 'minimax-m3';
}

function isMiniMaxM2Model(modelName: string): boolean {
  return /^minimax-m2(?:\.\d)?(?:-highspeed)?$/i.test(modelName.trim());
}
